import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateInputMDY from "@/components/DateInputMDY";
import { toast } from "sonner";
import { Plus, Trash2, Save, KeyRound, Copy, RefreshCw, Pencil, BookOpen, Music, Drama, Users as UsersIcon, Building2, Upload, ImageIcon, X, UserX } from "lucide-react";
import CharacterGroupsSection from "@/components/CharacterGroupsSection";
import Avatar from "@/components/Avatar";
import { fmtDate } from "@/lib/format";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };
const ROLES = ["org_admin", "producer", "production_staff", "support_staff", "cast"];
const VOICE_TYPES = ["", "Soprano", "Mezzo", "Alto", "Tenor", "Baritone", "Bass"];

export default function Users() {
  const { user, productionId } = useAuth();
  // Both org_admin and super_admin can manage users on this page.
  // super_admin previously fell through as false and lost the Role editor.
  const isAdmin = user.role === "org_admin" || user.role === "super_admin";
  const isSuperAdmin = user.role === "super_admin";
  const [members, setMembers] = useState([]);
  const [prod, setProd] = useState({ title: "", tagline: "", venue: "", opening_night: "", director: "" });
  const [scenes, setScenes] = useState([]);
  const [chars, setChars] = useState([]);

  const load = async () => {
    if (!productionId) return;
    const [m, p, s, c] = await Promise.all([
      api.get(`/productions/${productionId}/members`),
      api.get(`/productions/${productionId}`),
      api.get("/scenes"),
      api.get("/characters"),
    ]);
    setMembers(m.data);
    setProd({
      title: p.data.title || "",
      tagline: p.data.tagline || "",
      venue: p.data.venue || "",
      opening_night: p.data.opening_night || "",
      director: p.data.director || "",
      email_from_name: p.data.email_from_name || "StageCall Notifications",
      email_from_address: p.data.email_from_address || "noreply@stagecall.app",
      enroll_code: p.data.enroll_code || "",
      status: p.data.status,
      theater_name: p.data.theater_name || "",
      theater_logo_url: p.data.theater_logo_url || null,
    });
    setScenes(s.data);
    setChars(c.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [productionId]);

  const saveProd = async () => {
    const { enroll_code, status, theater_logo_url, ...updates } = prod;
    await api.patch(`/productions/${productionId}`, updates);
    toast.success("Production updated");
    load();
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/productions/${productionId}/theater-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Theatre logo uploaded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const removeLogo = async () => {
    if (!confirm("Remove the theatre logo?")) return;
    try {
      await api.delete(`/productions/${productionId}/theater-logo`);
      toast.success("Logo removed");
      load();
    } catch { toast.error("Could not remove"); }
  };
  const regen = async () => {
    if (!isAdmin) return;
    const r = await api.post(`/productions/${productionId}/regenerate-code`);
    toast.success(`New code: ${r.data.enroll_code}`);
    load();
  };
  const copyCode = () => {
    try { navigator.clipboard?.writeText(prod.enroll_code || ""); toast.success("Enrollment code copied"); }
    catch { toast.success("Code: " + (prod.enroll_code || "")); }
  };

  return (
    <div className="space-y-8 animate-fade-up" data-testid="users-page">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Setup</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Users &amp; Settings</h1>
        <p className="mt-1 text-zinc-400 text-sm">Production details, show setup (scenes, songs, characters), and company roster.</p>
      </header>

      {/* Production details */}
      <section className="rounded-lg border border-white/10 bg-[#121212] p-6">
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-4">Production details</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Title</Label><Input value={prod.title || ""} onChange={(e) => setProd({ ...prod, title: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Director</Label><Input value={prod.director || ""} onChange={(e) => setProd({ ...prod, director: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Venue</Label><Input value={prod.venue || ""} onChange={(e) => setProd({ ...prod, venue: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Opening night</Label><DateInputMDY value={prod.opening_night || ""} onChange={(v) => setProd({ ...prod, opening_night: v })} /></div>
          <div className="md:col-span-2"><Label>Tagline</Label><Input value={prod.tagline || ""} onChange={(e) => setProd({ ...prod, tagline: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
        </div>
        <div className="mt-4 flex justify-end">
          <button data-testid="save-production-btn" onClick={saveProd} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5"><Save className="w-4 h-4" /> Save production</button>
        </div>

        <div className="mt-4 rounded-md border border-gold/30 bg-gold/5 p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-gold" />
            <div className="text-[10px] uppercase tracking-widest text-gold">Enrollment code</div>
          </div>
          <code data-testid="settings-enroll-code" className="font-mono text-gold text-lg tracking-[0.3em]">{prod.enroll_code}</code>
          <button data-testid="settings-copy-code" onClick={copyCode} className="p-1.5 rounded border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5"><Copy className="w-3.5 h-3.5" /></button>
          {isAdmin && <button data-testid="settings-regen-code" onClick={regen} className="p-1.5 rounded border border-white/15 text-zinc-300 hover:text-white hover:bg-white/5"><RefreshCw className="w-3.5 h-3.5" /></button>}
          <span className="text-xs text-zinc-500">Share this code so cast &amp; crew can self-enroll from the login screen.</span>
        </div>

        {/* Email settings */}
        <div className="mt-6" data-testid="email-settings">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2">Email settings</div>
          <p className="text-xs text-zinc-500 mb-3">Announcements &amp; cast-note digests are sent from this address.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>From name</Label>
              <Input data-testid="email-from-name-input" value={prod.email_from_name || ""} onChange={(e) => setProd({ ...prod, email_from_name: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="StageCall Notifications" />
            </div>
            <div>
              <Label>From address</Label>
              <Input data-testid="email-from-address-input" value={prod.email_from_address || ""} onChange={(e) => setProd({ ...prod, email_from_address: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="noreply@stagecall.app" />
            </div>
          </div>
          <div className="mt-2 text-[11px] text-amber-400 uppercase tracking-widest">Mock delivery — logged only. Real SMTP setup coming later.</div>
        </div>

        {/* Theatre branding */}
        <div className="mt-6" data-testid="theater-branding-section">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-2 inline-flex items-center gap-1.5">
            <Building2 className="w-3.5 h-3.5" /> Theatre branding
          </div>
          <p className="text-xs text-zinc-500 mb-3">
            Upload your theatre&apos;s logo and set a display name. Appears on the sidebar, the public audition and judge pages, and printed call sheets.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
            {/* Logo preview + upload */}
            <div className="space-y-2">
              <div className="aspect-square rounded-lg border border-white/10 bg-[#0A0A0A] grid place-items-center overflow-hidden relative">
                {prod.theater_logo_url ? (
                  <img src={prod.theater_logo_url} alt="Theatre logo" className="w-full h-full object-contain p-3" data-testid="theater-logo-preview" />
                ) : (
                  <div className="text-center text-zinc-600 px-4">
                    <ImageIcon className="w-8 h-8 mx-auto" />
                    <div className="mt-2 text-[10px] uppercase tracking-widest">No logo uploaded</div>
                  </div>
                )}
                {prod.theater_logo_url && isAdmin && (
                  <button data-testid="remove-theater-logo" onClick={removeLogo}
                    title="Remove logo"
                    className="absolute top-2 right-2 w-7 h-7 rounded grid place-items-center bg-black/70 text-red-300 border border-red-500/40 hover:bg-red-500/10">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <label className="w-full h-10 rounded-md border border-gold/40 text-gold hover:bg-gold/10 cursor-pointer text-sm inline-flex items-center justify-center gap-1.5" data-testid="theater-logo-upload-label">
                <Upload className="w-4 h-4" /> {prod.theater_logo_url ? "Replace logo" : "Upload logo"}
                <input data-testid="theater-logo-file" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
              <div className="text-[10px] text-zinc-500 leading-relaxed">PNG · JPG · WEBP · SVG. Up to 4&nbsp;MB. Square logos display best.</div>
            </div>
            {/* Name + preview snippet */}
            <div className="space-y-3">
              <div>
                <Label>Theatre name</Label>
                <Input data-testid="theater-name-input" value={prod.theater_name || ""} onChange={(e) => setProd({ ...prod, theater_name: e.target.value })}
                  placeholder="Broadway Repertory · Regional Playhouse · …" className="bg-[#0A0A0A] border-white/10" />
                <div className="text-[10px] text-zinc-500 mt-1">Shown next to the logo in the sidebar and on public pages.</div>
              </div>
              <div className="rounded-md border border-white/10 bg-[#0A0A0A] p-3">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Sidebar preview</div>
                <div className="flex items-center gap-2">
                  {prod.theater_logo_url ? (
                    <img src={prod.theater_logo_url} alt="preview" className="w-10 h-10 rounded object-contain bg-[#121212] border border-white/10 p-0.5" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-[#121212] border border-white/10 grid place-items-center text-zinc-600">
                      <Building2 className="w-4 h-4" />
                    </div>
                  )}
                  <div>
                    <div className="font-display text-lg leading-none">{prod.theater_name || "Your theatre"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Powered by StageCall</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Show setup: Scenes, Songs, Characters */}
      <section data-testid="show-setup">
        <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3">Show setup</div>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <SceneList
            title="Scenes"
            icon={BookOpen}
            testid="setup-scenes"
            items={scenes.filter((s) => !s.is_song)}
            defaultIsSong={false}
            reload={load}
          />
          <SceneList
            title="Songs"
            icon={Music}
            testid="setup-songs"
            items={scenes.filter((s) => s.is_song)}
            defaultIsSong={true}
            reload={load}
          />
          <CharacterList
            testid="setup-characters"
            items={chars}
            members={members}
            reload={load}
          />
        </div>
      </section>

      {/* Company */}
      <CompanySection members={members} reload={load} isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} currentUserId={user.id} currentUserRole={user.role} />

      {/* Iter 73 — Ensemble & character groups editor */}
      <CharacterGroupsSection productionId={productionId} castUsers={members.filter((m) => m.role === "cast")} />
    </div>
  );
}

function SceneList({ title, icon: Icon, testid, items, defaultIsSong, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", act: "1", is_song: defaultIsSong, pages: "", notes: "" });

  const startCreate = () => {
    setEditing(null);
    setForm({ name: "", act: "1", is_song: defaultIsSong, pages: "", notes: "" });
    setOpen(true);
  };
  const startEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, act: s.act || "1", is_song: !!s.is_song, pages: s.pages || "", notes: s.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = { ...form, is_song: defaultIsSong, characters: editing?.characters || [] };
    if (editing) {
      await api.patch(`/scenes/${editing.id}`, payload);
      toast.success("Updated");
    } else {
      await api.post("/scenes", payload);
      toast.success(`${defaultIsSong ? "Song" : "Scene"} added`);
    }
    setOpen(false);
    reload();
  };
  const del = async (id) => {
    if (!confirm(`Delete this ${defaultIsSong ? "song" : "scene"}?`)) return;
    await api.delete(`/scenes/${id}`);
    reload();
  };

  return (
    <div data-testid={testid} className="rounded-lg border border-white/10 bg-[#121212] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/10 text-gold"><Icon className="w-4 h-4" /></div>
          <div className="font-display text-lg">{title}</div>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">{items.length}</span>
        </div>
        <button data-testid={`${testid}-add-btn`} onClick={startCreate} style={goldBtn} className="h-8 px-3 rounded-md font-semibold text-xs hover:brightness-110 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Add</button>
      </div>
      <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto">
        {items.length === 0 && <div className="py-5 text-center text-xs text-zinc-500">None yet.</div>}
        {items.map((s) => (
          <div key={s.id} className="py-2.5 flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-8">Act {s.act}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{s.name}</div>
              {s.pages && <div className="text-[11px] text-zinc-500">Pages {s.pages}</div>}
            </div>
            <button data-testid={`${testid}-edit-${s.id}`} onClick={() => startEdit(s)} title="Edit" className="text-zinc-400 hover:text-gold p-1"><Pencil className="w-3.5 h-3.5" /></button>
            <button data-testid={`${testid}-del-${s.id}`} onClick={() => del(s.id)} title="Delete" className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit" : "Add"} {defaultIsSong ? "song" : "scene"}</DialogTitle>
            <DialogDescription className="text-zinc-500">Give it a name and act. You can edit anytime.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid={`${testid}-name-input`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus className="bg-[#0A0A0A] border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Act</Label><Input value={form.act} onChange={(e) => setForm({ ...form, act: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div><Label>Pages</Label><Input value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          </div>
          <DialogFooter>
            <button data-testid={`${testid}-save-btn`} onClick={save} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CharacterList({ testid, items, members, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", voice_type: "", actor_user_id: "", description: "" });
  const castMembers = useMemo(() => members.filter((m) => m.role === "cast"), [members]);
  const userMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Character name required");
    const payload = { ...form, actor_user_id: form.actor_user_id || null };
    if (editing) {
      await api.patch(`/characters/${editing.id}`, payload);
      toast.success("Updated");
    } else {
      await api.post("/characters", payload);
      toast.success("Character added");
    }
    setOpen(false);
    setEditing(null);
    setForm({ name: "", voice_type: "", actor_user_id: "", description: "" });
    reload();
  };
  const del = async (id) => {
    if (!confirm("Delete this character?")) return;
    await api.delete(`/characters/${id}`);
    reload();
  };

  return (
    <div data-testid={testid} className="rounded-lg border border-white/10 bg-[#121212] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/10 text-gold"><Drama className="w-4 h-4" /></div>
          <div className="font-display text-lg">Characters</div>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">{items.length}</span>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <button data-testid="setup-characters-add-btn" onClick={() => { setEditing(null); setForm({ name: "", voice_type: "", actor_user_id: "", description: "" }); }} style={goldBtn} className="h-8 px-3 rounded-md font-semibold text-xs hover:brightness-110 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />Add</button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit" : "Add"} character</DialogTitle>
              <DialogDescription className="text-zinc-500">Optionally assign an actor. Assigning updates their profile character.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input data-testid="setup-characters-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus className="bg-[#0A0A0A] border-white/10" placeholder="e.g. The Baker's Wife" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Voice type</Label>
                  <Select value={form.voice_type || "none"} onValueChange={(v) => setForm({ ...form, voice_type: v === "none" ? "" : v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                      <SelectItem value="none">—</SelectItem>
                      {VOICE_TYPES.filter(Boolean).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Actor</Label>
                  <Select value={form.actor_user_id || "none"} onValueChange={(v) => setForm({ ...form, actor_user_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="setup-characters-actor-select" className="bg-[#0A0A0A] border-white/10"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                      <SelectItem value="none">— unassigned —</SelectItem>
                      {castMembers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            </div>
            <DialogFooter>
              <button data-testid="setup-characters-save-btn" onClick={save} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Save</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="divide-y divide-white/5 max-h-[420px] overflow-y-auto">
        {items.length === 0 && <div className="py-5 text-center text-xs text-zinc-500">No characters yet.</div>}
        {items.map((c) => {
          const actor = c.actor_user_id ? userMap[c.actor_user_id] : null;
          return (
            <div key={c.id} className="py-2.5 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{c.name}
                  {c.voice_type && <span className="text-xs text-zinc-500 ml-2">· {c.voice_type}</span>}
                </div>
                <div className="text-[11px] text-zinc-500 truncate">{actor ? `Played by ${actor.name}` : "Unassigned"}</div>
              </div>
              <button
                data-testid={`setup-characters-edit-${c.id}`}
                onClick={() => { setEditing(c); setForm({ name: c.name, voice_type: c.voice_type || "", actor_user_id: c.actor_user_id || "", description: c.description || "" }); setOpen(true); }}
                title="Edit"
                className="text-zinc-400 hover:text-gold p-1"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`setup-characters-del-${c.id}`} onClick={() => del(c.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompanySection({ members, reload, isAdmin, isSuperAdmin, currentUserId, currentUserRole }) {
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "cast", phone: "", character: "" });
  const [editUser, setEditUser] = useState(null);
  const { productionId } = useAuth();

  const create = async () => {
    if (!form.email || !form.password || !form.name) return toast.error("Email, password and name required");
    try {
      await api.post("/users", { ...form, production_id: productionId });
      setOpenNew(false);
      setForm({ email: "", password: "", name: "", role: "cast", phone: "", character: "" });
      reload();
      toast.success("User created & added to production");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
  };
  const removeMember = async (userId, userName) => {
    if (userId === currentUserId) return toast.error("You can't remove yourself.");
    if (!confirm(`Remove ${userName || "this user"} from the production?\n\nTheir account stays intact — this only revokes access to this show.`)) return;
    try {
      await api.delete(`/productions/${productionId}/members/${userId}`);
      toast.success("Removed from production");
      reload();
    } catch (e) {
      // Surface the exact server reason so the trash icon never looks
      // "broken" — the previous silent 403 was the "OK / Remove doesn't do
      // anything" bug reported in Iter 72.
      toast.error(e.response?.data?.detail || "Could not remove — check your permissions");
    }
  };
  // Full account delete — only shown for cast / support_staff via the
  // "Delete user" affordance in the row menu. Cascade-cleans their audition
  // record but keeps historical notes/attendance as "Deleted user".
  const deleteUser = async (u) => {
    if (u.id === currentUserId) return toast.error("You can't delete yourself.");
    if (!confirm(`Delete ${u.name} from the entire system?\n\nThis removes their login, membership on every show, and their audition record. Historical notes and attendance stay for the audit trail. Continue?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete — check your permissions");
    }
  };

  return (
    <section className="rounded-lg border border-white/10 bg-[#121212]">
      <div className="p-5 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <UsersIcon className="w-4 h-4 text-gold" />
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Company</div>
          </div>
          <div className="text-sm text-zinc-400 mt-0.5">{members.length} member{members.length === 1 ? "" : "s"}</div>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <button data-testid="new-user-btn" style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5"><Plus className="w-4 h-4" />New user</button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-display">Add user to production</DialogTitle>
              <DialogDescription className="text-zinc-500">Create an account and add it as a member of this production.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="new-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {ROLES.filter((r) => isAdmin || (r !== "org_admin" && r !== "producer")).map((r) => <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Email</Label><Input data-testid="new-user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div><Label>Password</Label><Input data-testid="new-user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div><Label>Character / Note</Label><Input value={form.character} onChange={(e) => setForm({ ...form, character: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            </div>
            <DialogFooter>
              <button data-testid="save-new-user-btn" onClick={create} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110">Create user</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="divide-y divide-white/5">
        {members.map((u) => (
          <div key={u.id} className="p-4 flex items-center gap-4">
            <Avatar user={u} size="w-10 h-10" refresh={u.avatar_path || 0} />
            <div className="flex-1 min-w-0">
              <div className="text-sm">{u.name} <span className="text-zinc-500 text-xs">· {u.email}</span></div>
              <div className="text-xs text-zinc-500 flex items-center flex-wrap gap-1 mt-0.5">
                {(() => {
                  const raw = u.character || u.production_character || "";
                  const roles = raw.split(" / ").map((s) => s.trim()).filter(Boolean);
                  if (roles.length === 0) return <span>—</span>;
                  return roles.map((r) => (
                    <span key={r} data-testid={`user-role-pill-${u.id}-${r}`} className="text-[10px] px-1.5 py-0.5 rounded border border-gold/30 bg-gold/5 text-gold uppercase tracking-widest">{r}</span>
                  ));
                })()}
                {u.phone && <span>· {u.phone}</span>}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-widest text-gold">{u.role}</span>
            <button data-testid={`edit-user-${u.id}`} onClick={() => setEditUser(u)} title="Edit contact info" className="text-zinc-400 hover:text-gold p-1.5 rounded border border-white/10 hover:border-gold/40 transition-colors"><Pencil className="w-4 h-4" /></button>
            <button
              data-testid={`del-user-${u.id}`}
              onClick={() => removeMember(u.id, u.name)}
              title="Remove from this production only (account stays)"
              className="text-zinc-500 hover:text-red-400 p-1.5"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            {/* Full-account delete — only shown for cast / support_staff so
                staff can clean up declined auditioners and misjoined
                accounts. Never surfaced for org_admins / producers /
                super_admins (the backend also blocks that path). */}
            {(u.role === "cast" || u.role === "support_staff") && (isAdmin || currentUserRole === "production_staff") && u.id !== currentUserId && (
              <button
                data-testid={`nuke-user-${u.id}`}
                onClick={() => deleteUser(u)}
                title="Delete account from the entire system"
                className="text-zinc-500 hover:text-red-500 p-1.5 rounded border border-transparent hover:border-red-500/30"
              >
                <UserX className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {editUser && (
        <EditUserDialog
          user={editUser}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); reload(); }}
        />
      )}
    </section>
  );
}

const ROLE_LABELS = {
  super_admin: "Super Admin",
  org_admin: "Org Admin",
  producer: "Producer",
  production_staff: "Production Staff",
  support_staff: "Support Staff",
  cast: "Cast",
};

function EditUserDialog({ user, isAdmin, isSuperAdmin, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    character: user.character || user.production_character || "",
    role: user.role || "cast",
    password: "",
  });
  const [saving, setSaving] = useState(false);
  const [avatarKey, setAvatarKey] = useState(user.avatar_path || Date.now());
  // Cast Character Editor — post-finalize role swap.
  const [allChars, setAllChars] = useState([]);
  const [addPick, setAddPick] = useState("");
  const isCast = user.role === "cast";
  // Iter 75 — ensemble group editor (mirrors the Cast roles pattern below)
  // + family performers section so admins can attach additional kids to
  // one login without opening the Ensembles page.
  const [allGroups, setAllGroups] = useState([]);       // every group in the production
  const [myGroupIds, setMyGroupIds] = useState([]);     // groups this cast member belongs to
  const [groupAddPick, setGroupAddPick] = useState("");
  const [family, setFamily] = useState([]);             // other performers under the same email
  const [newPerformer, setNewPerformer] = useState({ name: "", character: "", phone: "" });
  const [addingPerformer, setAddingPerformer] = useState(false);

  useEffect(() => {
    if (!isCast || !isAdmin) return;
    api.get("/characters").then((r) => setAllChars(r.data)).catch(() => {});
    // Groups + memberships. We infer membership by cross-referencing the
    // group's direct_user_ids field returned by GET /character-groups.
    api.get("/character-groups").then((r) => {
      const gs = r.data || [];
      setAllGroups(gs);
      setMyGroupIds(gs.filter((g) => (g.direct_user_ids || []).includes(user.id)).map((g) => g.id));
    }).catch(() => {});
    // Family performers linked to this login.
    api.get(`/users/${user.id}/performers`).then((r) => setFamily(r.data || [])).catch(() => {});
  }, [isCast, isAdmin, user.id]);
  const myChars = allChars.filter((c) => c.actor_user_id === user.id);
  const availableChars = allChars.filter((c) => !c.actor_user_id || c.actor_user_id === "");
  const availableGroups = allGroups.filter((g) => !myGroupIds.includes(g.id));
  const setActor = async (charId, actorUserId) => {
    try {
      const { data } = await api.patch(`/characters/${charId}/actor`, { actor_user_id: actorUserId || null });
      // Reflect the swap locally so the dialog doesn't need a full refetch.
      setAllChars((cur) => cur.map((c) => (c.id === charId ? { ...c, actor_user_id: data.actor_user_id } : c)));
      // Refresh the joined display string in the form.
      const next = allChars
        .filter((c) => (c.id === charId ? actorUserId === user.id : c.actor_user_id === user.id))
        .map((c) => c.name)
        .sort()
        .join(" / ");
      setForm((f) => ({ ...f, character: next }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update role");
    }
  };
  // Iter 75 — ensemble group membership toggles. Reuse the character-groups
  // members endpoints instead of any bespoke wiring; keeps a single source
  // of truth with the People → Ensembles page.
  const addToGroup = async (gid) => {
    try {
      await api.post(`/character-groups/${gid}/members`, { user_ids: [user.id] });
      setMyGroupIds((cur) => [...cur, gid]);
      setGroupAddPick("");
      toast.success("Added to ensemble");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add to ensemble");
    }
  };
  const removeFromGroup = async (gid) => {
    try {
      await api.delete(`/character-groups/${gid}/members/${user.id}`);
      setMyGroupIds((cur) => cur.filter((x) => x !== gid));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove from ensemble");
    }
  };
  const addPerformer = async () => {
    if (!newPerformer.name.trim()) return toast.error("Name is required");
    setAddingPerformer(true);
    try {
      const { data } = await api.post(`/users/${user.id}/performers`, newPerformer);
      setFamily((cur) => [...cur, data]);
      setNewPerformer({ name: "", character: "", phone: "" });
      toast.success(`${data.name} added to this login`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add performer");
    } finally { setAddingPerformer(false); }
  };
  const removePerformer = async (childId, childName) => {
    if (!confirm(`Remove ${childName || "this performer"} from this family?\n\nThis deletes their profile, calls, conflicts, and attendance — but the parent login stays intact.`)) return;
    try {
      await api.delete(`/users/${user.id}/performers/${childId}`);
      setFamily((cur) => cur.filter((c) => c.id !== childId));
      toast.success("Performer removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove performer");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const upd = { name: form.name, email: form.email, phone: form.phone, character: form.character };
      if (isAdmin) {
        if (form.role) upd.role = form.role;
        if (form.password) upd.password = form.password;
      }
      await api.patch(`/users/${user.id}`, upd);
      toast.success("Updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/users/${user.id}/avatar`, fd);
      toast.success("Headshot updated");
      setAvatarKey(Date.now());
    } catch {
      toast.error("Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit user</DialogTitle>
          <DialogDescription className="text-zinc-500">Update contact info and (admin only) role or password.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Avatar user={{ ...user, avatar_path: user.avatar_path }} size="w-16 h-16" textClass="text-xl" refresh={avatarKey} />
            <label className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full grid place-items-center bg-gold text-black border-2 border-[#121212] cursor-pointer hover:brightness-110">
              <span className="text-[10px]">+</span>
              <input type="file" accept="image/*" className="hidden" data-testid={`edit-user-avatar-${user.id}`} onChange={uploadAvatar} />
            </label>
          </div>
          <div>
            <div className="font-display text-lg">{user.name}</div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest" data-testid="edit-user-current-role">
              {ROLE_LABELS[user.role] || user.role}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Full name</Label><Input data-testid="edit-user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Email</Label><Input data-testid="edit-user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div><Label>Phone</Label><Input data-testid="edit-user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          <div className="col-span-2"><Label>Character / Note</Label><Input data-testid="edit-user-character-input" value={form.character} onChange={(e) => setForm({ ...form, character: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
          {/* Cast Character Editor — only shown for cast members and admins. */}
          {isCast && isAdmin && (
            <div className="col-span-2 rounded-md border border-white/10 bg-[#0F0F0F] p-3 space-y-2" data-testid="cast-role-editor">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Cast roles</div>
                {myChars.length > 0 && (
                  <div className="text-[10px] text-zinc-500">{myChars.length} assigned</div>
                )}
              </div>
              {myChars.length === 0 ? (
                <div className="text-xs text-zinc-500 py-1">Not currently cast in any role.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="cast-role-current">
                  {myChars.map((c) => (
                    <span key={c.id} className="text-[11px] pl-2 pr-1 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold inline-flex items-center gap-1.5">
                      {c.name}
                      <button
                        type="button"
                        data-testid={`cast-role-remove-${c.id}`}
                        onClick={() => setActor(c.id, null)}
                        aria-label={`Remove ${c.name}`}
                        className="w-4 h-4 rounded-full hover:bg-gold/25 inline-flex items-center justify-center leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {availableChars.length > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <Select value={addPick} onValueChange={setAddPick}>
                    <SelectTrigger data-testid="cast-role-add-select" className="bg-[#0A0A0A] border-white/10 h-9 text-xs flex-1"><SelectValue placeholder="Add another role…" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                      {availableChars.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.voice_type ? ` · ${c.voice_type}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    data-testid="cast-role-add-btn"
                    disabled={!addPick}
                    onClick={() => { setActor(addPick, user.id); setAddPick(""); }}
                    className="h-9 px-3 rounded border border-white/15 text-xs text-zinc-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              )}
              <div className="text-[11px] text-zinc-500 pt-1">Swaps take effect immediately — no re-finalize needed.</div>
            </div>
          )}

          {/* Iter 75 — Ensemble groups editor. Mirrors the Cast roles pill
              pattern above so staff can attach/detach groups from a
              performer's profile without hopping to People → Ensembles. */}
          {isCast && isAdmin && (
            <div className="col-span-2 rounded-md border border-white/10 bg-[#0F0F0F] p-3 space-y-2" data-testid="ensemble-group-editor">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ensemble groups</div>
                {myGroupIds.length > 0 && (
                  <div className="text-[10px] text-zinc-500">{myGroupIds.length} assigned</div>
                )}
              </div>
              {myGroupIds.length === 0 ? (
                <div className="text-xs text-zinc-500 py-1">Not in any ensemble group.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="ensemble-group-current">
                  {allGroups.filter((g) => myGroupIds.includes(g.id)).map((g) => (
                    <span key={g.id} className="text-[11px] pl-2 pr-1 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold inline-flex items-center gap-1.5">
                      {g.parent_group_id ? "↳ " : ""}{g.name}
                      <button
                        type="button"
                        data-testid={`ensemble-group-remove-${g.id}`}
                        onClick={() => removeFromGroup(g.id)}
                        aria-label={`Remove from ${g.name}`}
                        className="w-4 h-4 rounded-full hover:bg-gold/25 inline-flex items-center justify-center leading-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {availableGroups.length > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <Select value={groupAddPick} onValueChange={setGroupAddPick}>
                    <SelectTrigger data-testid="ensemble-group-add-select" className="bg-[#0A0A0A] border-white/10 h-9 text-xs flex-1"><SelectValue placeholder="Add to another ensemble…" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                      {availableGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.parent_group_id ? "↳ " : ""}{g.name} · {g.total_user_count} member{g.total_user_count === 1 ? "" : "s"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    data-testid="ensemble-group-add-btn"
                    disabled={!groupAddPick}
                    onClick={() => addToGroup(groupAddPick)}
                    className="h-9 px-3 rounded border border-white/15 text-xs text-zinc-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
              )}
              <div className="text-[11px] text-zinc-500 pt-1">Groups can also be called in bulk from any rehearsal.</div>
            </div>
          )}

          {/* Iter 75 — Family performers. Only meaningful for cast rows;
              lets one login (a parent) represent multiple performers who
              share the email. Each child gets their own schedule/calls/
              conflicts. */}
          {isCast && isAdmin && !user.family_head_user_id && (
            <div className="col-span-2 rounded-md border border-white/10 bg-[#0F0F0F] p-3 space-y-2" data-testid="family-performers-editor">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Other performers on this email</div>
                {family.length > 0 && (
                  <div className="text-[10px] text-zinc-500">{family.length} additional</div>
                )}
              </div>
              {family.length === 0 ? (
                <div className="text-xs text-zinc-500 py-1">Only this performer uses this login. Add another below if a parent enrolls multiple kids.</div>
              ) : (
                <div className="space-y-1.5" data-testid="family-performer-list">
                  {family.map((p) => (
                    <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded border border-white/10 bg-[#0A0A0A]">
                      <Avatar user={p} size="w-6 h-6" textClass="text-[10px]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs">{p.name}</div>
                        {p.character && <div className="text-[10px] text-zinc-500 truncate">{p.character}</div>}
                      </div>
                      <button
                        type="button"
                        data-testid={`family-performer-remove-${p.id}`}
                        onClick={() => removePerformer(p.id, p.name)}
                        title="Remove this performer"
                        className="text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 pt-1">
                <Input data-testid="family-performer-name" value={newPerformer.name} onChange={(e) => setNewPerformer({ ...newPerformer, name: e.target.value })} placeholder="Performer name" className="bg-[#0A0A0A] border-white/10 h-8 text-xs col-span-2" />
                <Input value={newPerformer.character} onChange={(e) => setNewPerformer({ ...newPerformer, character: e.target.value })} placeholder="Character (optional)" className="bg-[#0A0A0A] border-white/10 h-8 text-xs" />
                <div className="col-span-3 flex justify-end">
                  <button
                    type="button"
                    data-testid="family-performer-add"
                    disabled={addingPerformer || !newPerformer.name.trim()}
                    onClick={addPerformer}
                    className="h-8 px-3 rounded border border-white/15 text-xs text-zinc-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    <Plus className="w-3 h-3" /> Add performer
                  </button>
                </div>
              </div>
              <div className="text-[11px] text-zinc-500 pt-1">The parent&apos;s login and password work for every performer here. Each performer gets their own schedule and conflicts.</div>
            </div>
          )}
          {isAdmin && (
            <>
              <div className="col-span-2">
                <Label className="flex items-center gap-2">
                  Role
                  {form.role !== user.role && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 uppercase tracking-widest">
                      Unsaved change
                    </span>
                  )}
                </Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="edit-user-role-select" className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {/* super_admin is only assignable by another super_admin — org_admins
                        should not be able to grant platform-level access. */}
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>)}
                    {isSuperAdmin && <SelectItem value="super_admin">{ROLE_LABELS.super_admin}</SelectItem>}
                  </SelectContent>
                </Select>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Changes the person&apos;s system-wide permissions across every production.
                </div>
              </div>
              <div className="col-span-2"><Label>Reset password <span className="text-zinc-600 text-xs">(optional)</span></Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            </>
          )}
        </div>
        <DialogFooter>
          <button data-testid="edit-user-save-btn" onClick={save} disabled={saving} style={goldBtn} className="h-9 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5">
            <Save className="w-4 h-4" /> Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
